import React, { ForwardedRef, PropsWithRef, ReactElement, ReactNode, useMemo, useRef } from 'react';
import { BaseRowData, DataProvider, DataProviderRowData } from '@adv-frontend/data-provider';
import { ListState } from 'react-stately';
import { useListBox, useOption } from 'react-aria';
import { ListLayout } from '@react-stately/layout';

import { TestProps } from '../../internal/types/testTypes';
import { VirtualList, VirtualListProps } from '../List/VirtualList';
import { ListItemProps } from '../List/ListItem';
import { useVirtualizer } from '../List/VirtualList/utils';
import { IconSelectSkinny } from '../../icons';

export type DataProviderRow<RowData extends BaseRowData> = ReturnType<DataProvider<RowData>['useRowData']>;

type ItemProps = Partial<PropsWithRef<ListItemProps>> & { id: string; ref: ForwardedRef<HTMLLIElement> };

export interface VirtualListBoxProps<RowData extends BaseRowData>
    extends TestProps,
        Omit<VirtualListProps<RowData>, 'children' | 'placeholder'> {
    /**
     * Стейт из вышестоящего компонента Select или Combobox для управления выбором
     */
    state: ListState<DataProviderRowData>;

    /**
     * Функция-рендерер для каждой строки данных
     * @param item строка данных из dataProvider
     * @param props пропсы для ListItem
     * @returns отрендеренный элемент
     * @example ```tsx
     * (item, props) => <ListItem {...props}>{item.data.name}</ListItem>
     * ```
     */
    children: (item: DataProviderRow<RowData>, props: ItemProps) => ReactElement | null;

    /**
     * aria-label для списка
     */
    'aria-label'?: string;

    /**
     * Секция, которая будет отображаться, если список пуст
     * @example emptySection={<ListItem isDisabled>Ничего не найдено</ListItem>}
     */
    emptySection?: ReactNode;
}

interface ItemRendererProps<RowData extends BaseRowData>
    extends Pick<VirtualListBoxProps<RowData>, 'state'>,
        TestProps {
    renderer: VirtualListBoxProps<RowData>['children'];
    item: DataProviderRow<RowData>;
}

const ItemRenderer = function ItemRenderer<RowData extends BaseRowData>(props: ItemRendererProps<RowData>) {
    const { renderer, state, item, 'data-testid': dataTestId = 'ListBox' } = props;
    const ref = useRef(null);
    const id = item.data?.id ?? '';
    // TODO DIRECT-243913 Из-за небезопасного доступа по id тут крашится страница, убрать isVirtualized после обновления
    const { isSelected } = useOption({ key: id, isVirtualized: false }, state, ref);
    const itemProps = {
        ref,
        id,
        onClick: () => {
            state.selectionManager.toggleSelection(id);
        },
        iconRight: isSelected ? IconSelectSkinny : undefined,
        isDisabled: state.disabledKeys.has(id),
        'data-testid': `${dataTestId}.${id}`,
        role: 'option',
    } satisfies ItemProps;

    if (!item) {
        return null;
    }

    return renderer(
        item,
        // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
        itemProps,
    );
};

/**
 * Компонент выбора из списка с виртуализацией и поддержкой dataProvider
 * @example
 * ```tsx
 * const dataProvider = api.useDataProvider();
 * const state = useListState({ items: dataProvider.getRows() });
 *
 * return <VirtualListBox dataProvider={dataProvider} state={state}>
 *  {item => <ListItem>{item.name}</ListItem>}
 * </VirtualListBox>
 * ```
 */
export const VirtualListBox = function VirtualListBox<RowData extends BaseRowData>(
    props: VirtualListBoxProps<RowData>,
) {
    const { state, size, emptySection } = props;
    const ref = useRef(null);
    const virtualizer = useVirtualizer({ size });
    const layout = useMemo(() => new ListLayout<DataProviderRowData>(virtualizer), [virtualizer]);

    useListBox(
        {
            ...props,
            isVirtualized: true,
            keyboardDelegate: layout,
        },
        state,
        ref,
    );

    return (
        <VirtualList {...props} placeholder={emptySection} ref={ref}>
            {item => (
                <ItemRenderer renderer={props.children} state={state} item={item} data-testid={props['data-testid']} />
            )}
        </VirtualList>
    );
};
